"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.apparelVariantName = void 0;
exports.apparelSku = apparelSku;
exports.apparelCombos = apparelCombos;
exports.apparelKeyOf = apparelKeyOf;
exports.reconcileApparelVariants = reconcileApparelVariants;
exports.axesFromVariants = axesFromVariants;
exports.resolveGridPrice = resolveGridPrice;
exports.applyGridPatch = applyGridPatch;
const productTypes_1 = require("./productTypes");
const axis = (axes, name) => [...(axes.find(a => a.name === name)?.values ?? [])].sort((a, b) => a.sortOrder - b.sortOrder).map(v => v.value);
const apparelVariantName = (color, size) => `${color} / ${size}`;
exports.apparelVariantName = apparelVariantName;
function apparelSku(productCode, color, size) {
    const code = color.replace(/[^A-Za-z]/g, '').slice(0, 3);
    return `${productCode}-${code}-${size}`.toUpperCase();
}
function apparelCombos(axes) {
    const sizes = axis(axes, productTypes_1.APPAREL_AXIS.SIZE);
    return axis(axes, productTypes_1.APPAREL_AXIS.COLOR).flatMap(color => sizes.map(size => ({ color, size })));
}
// "Color|Size" for a variant, from options or (legacy) its "Color / Size" name.
function apparelKeyOf(v) {
    const c = v.options?.[productTypes_1.APPAREL_AXIS.COLOR];
    const s = v.options?.[productTypes_1.APPAREL_AXIS.SIZE];
    if (c && s)
        return `${c}|${s}`;
    return v.displayName.includes(' / ') ? v.displayName.split(' / ').join('|') : null;
}
// The grid never deletes or deactivates: a saved size whose colour/size is no
// longer in the axes is reported as `orphaned` (the editor refuses such a
// removal); deleting a size is the normal one-variant delete in the editor.
// Sizes already marked for deletion are never re-created or updated.
function reconcileApparelVariants(axes, existing) {
    const wanted = new Map(apparelCombos(axes).map(c => [`${c.color}|${c.size}`, c]));
    const out = { create: [], keep: [], orphaned: [], pendingDelete: [] };
    const seen = new Set();
    for (const v of existing) {
        const k = apparelKeyOf(v);
        if (!k || seen.has(k))
            continue; // unmatched or duplicate legacy rows are left alone
        if (v.status === 'inactive' || v.status === 'deleted')
            continue;
        seen.add(k);
        if (v.status === 'pending_delete')
            out.pendingDelete.push(v.variantId);
        else if (wanted.has(k))
            out.keep.push(v.variantId);
        else
            out.orphaned.push(v.variantId);
    }
    for (const [k, c] of wanted)
        if (!seen.has(k))
            out.create.push(c);
    return out;
}
// A clothing product with no saved optionAxes (import, business transfer):
// rebuild the colour and size lists from its variants, in first-seen order,
// so nothing already on sale drops out of the grid.
function axesFromVariants(variants) {
    const colors = [];
    const sizes = [];
    for (const v of variants) {
        const k = apparelKeyOf(v);
        if (!k)
            continue;
        const [c, s] = k.split('|');
        if (!colors.includes(c))
            colors.push(c);
        if (!sizes.includes(s))
            sizes.push(s);
    }
    return [
        { name: productTypes_1.APPAREL_AXIS.COLOR, values: colors.map((value, sortOrder) => ({ value, sortOrder })) },
        { name: productTypes_1.APPAREL_AXIS.SIZE, values: sizes.map((value, sortOrder) => ({ value, sortOrder })) },
    ];
}
function resolveGridPrice(g, color, size) {
    const cell = g.cell[`${color}|${size}`];
    if (cell != null)
        return { price: cell, source: 'cell' };
    if (g.color[color] != null)
        return { price: g.color[color], source: 'color' };
    return { price: g.base, source: 'base' };
}
// Merge a grid edit into one channel config. Only the fields the grid owns
// change; everything else (fees, commission, cash discount, enabled…) survives.
function applyGridPatch(existing, patch) {
    const base = existing ?? { enabled: true, price: 0, priceAuto: false, taxIds: [], feeIds: [], promotionId: null };
    const out = { ...base };
    if (patch.price !== undefined) {
        out.price = patch.price;
        out.priceAuto = false;
    }
    if (patch.taxIds !== undefined)
        out.taxIds = patch.taxIds;
    if (patch.promotionId !== undefined)
        out.promotionId = patch.promotionId;
    return out;
}
//# sourceMappingURL=apparel.js.map